<script setup lang="ts">
const { user } = useAuth()
const { fab: pageFab } = useHeaderMenu()
const chat = useChatFab()
const route = useRoute()

// На мобильных страницы могут показывать свой FAB (см. layouts/default.vue,
// useHeaderMenu.setFab) в том же правом нижнем углу — поднимаем виджет чата
// повыше, чтобы кнопки не перекрывались.
const rootStyle = computed(() => ({
  bottom: `calc(${pageFab.value ? 92 : 24}px + env(safe-area-inset-bottom))`
}))

function confirmDeleteConversation(id: number) {
  if (confirm('Удалить этот чат? Это действие необратимо.')) {
    chat.deleteConversation(id)
  }
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`
}

// Ширина полноразмерного чата тянется мышью/тачем за левый край панели.
// Курсор в чате прижат к правому краю экрана, поэтому ширина = расстояние
// от указателя до правого края окна.
function onResizeStart(event: PointerEvent) {
  if (!chat.isExpanded.value) return
  event.preventDefault()
  chat.isResizing.value = true
  const prevUserSelect = document.body.style.userSelect
  document.body.style.userSelect = 'none'

  function onMove(e: PointerEvent) {
    chat.setChatWidth(window.innerWidth - e.clientX)
  }
  function onUp() {
    chat.isResizing.value = false
    document.body.style.userSelect = prevUserSelect
    chat.persistChatWidth()
    window.removeEventListener('pointermove', onMove)
    window.removeEventListener('pointerup', onUp)
  }
  window.addEventListener('pointermove', onMove)
  window.addEventListener('pointerup', onUp, { once: true })
}

// Ссылка на саму панель — нужна для onDragStart, чтобы взять её текущий
// прямоугольник на экране (getBoundingClientRect) в момент начала перетаскивания.
const panelEl = ref<HTMLElement | null>(null)

// Перетаскивание доступно только в маленьком режиме (не полноразмерном сайдбаре) —
// хватаем за шапку панели (или за саму плашку, когда свёрнуто) и двигаем мышью/тачем,
// ограничивая координаты границами окна. onTap (если передан) срабатывает вместо
// перетаскивания, если указатель почти не сдвинулся — так плашку можно и тащить, и
// открыть простым тапом одним и тем же обработчиком (обычный @click после @pointerdown
// с drag так просто не совмещается: click всё равно стрельнёт после любого перетаскивания).
function startDrag(event: PointerEvent, onTap?: () => void) {
  if (chat.isExpanded.value) return
  // Ниже sm: маленький режим всё равно полноэкранный (нет отдельного w/h для
  // перетаскиваемого состояния на такой ширине) — перетаскивание там не нужно.
  if (window.innerWidth < 640) return
  if ((event.target as HTMLElement).closest('button')) return
  if (!panelEl.value) return
  event.preventDefault()

  const rect = panelEl.value.getBoundingClientRect()
  // Первое перетаскивание переключает с "приколото к правому нижнему углу" на
  // фиксированные top/left — стартуем с фактического текущего места на экране,
  // чтобы панель не дёргалась в момент захвата.
  if (!chat.position.value) {
    chat.position.value = { x: rect.left, y: rect.top }
  }

  const startX = event.clientX
  const startY = event.clientY
  let moved = false
  chat.isDragging.value = true
  const offsetX = event.clientX - rect.left
  const offsetY = event.clientY - rect.top
  const prevUserSelect = document.body.style.userSelect
  document.body.style.userSelect = 'none'

  function onMove(e: PointerEvent) {
    if (!moved && Math.hypot(e.clientX - startX, e.clientY - startY) > 4) moved = true
    const maxX = Math.max(0, window.innerWidth - rect.width)
    const maxY = Math.max(0, window.innerHeight - rect.height)
    chat.position.value = {
      x: Math.min(Math.max(0, e.clientX - offsetX), maxX),
      y: Math.min(Math.max(0, e.clientY - offsetY), maxY),
    }
  }
  function onUp() {
    chat.isDragging.value = false
    document.body.style.userSelect = prevUserSelect
    window.removeEventListener('pointermove', onMove)
    window.removeEventListener('pointerup', onUp)
    if (!moved) onTap?.()
  }
  window.addEventListener('pointermove', onMove)
  window.addEventListener('pointerup', onUp, { once: true })
}

function onDragStart(event: PointerEvent) {
  startDrag(event)
}

function onMinimizedPointerDown(event: PointerEvent) {
  startDrag(event, () => chat.restore())
}

// После перетаскивания панель позиционируется от viewport (fixed top/left), а не
// "приколота" к правому нижнему углу через sm:absolute/bottom-0/right-0 — те классы
// остаются в разметке, но инлайновый style здесь их перебивает (position важнее
// specificity класса). До первого перетаскивания (position === null) ничего не
// добавляет — панель ведёт себя как раньше.
const panelStyle = computed(() => {
  const style: Record<string, string> = { '--chat-width': chat.chatWidth.value + 'px' }
  if (!chat.isExpanded.value && chat.position.value) {
    style.position = 'fixed'
    style.top = `${chat.position.value.y}px`
    style.left = `${chat.position.value.x}px`
    style.right = 'auto'
    style.bottom = 'auto'
  }
  return style
})

// Действия на детальной странице тендера (route: pages/tenders/[id].vue).
// Статичные подстраницы вроде /tenders/completed идут отдельными файлами
// (pages/tenders/completed/index.vue и т.п.), поэтому по имени роута
// однозначно отличаем именно детальную страницу от списков.
const isTenderDetailPage = computed(() => route.name === 'tenders-id')
const tenderId = computed(() => (isTenderDetailPage.value ? String(route.params.id) : null))

type TenderQuickActionKind = 'all' | 'docs' | 'find-products' | 'check-products'
const TENDER_QUICK_ACTIONS: { kind: TenderQuickActionKind; label: string; icon: string }[] = [
  { kind: 'all', label: 'Запустить всё', icon: 'lucide:zap' },
  { kind: 'docs', label: 'Изучить документацию', icon: 'lucide:file-search' },
  { kind: 'find-products', label: 'Подобрать товар', icon: 'lucide:search' },
  { kind: 'check-products', label: 'Проверить товар', icon: 'lucide:check-circle' },
]
const quickActionLoading = ref<TenderQuickActionKind | null>(null)
const quickActionsCollapsed = ref(false)
watch(tenderId, () => { quickActionsCollapsed.value = false })

// "Проверить товар" имеет смысл только если по тендеру уже выбран хотя бы один товар —
// иначе показываем только "Подобрать товар". Проверяем независимо от runTenderAction
// (тот фетчит тендер заново по клику ради актуальных данных для промпта), просто чтобы
// решить, какие кнопки показывать.
const tenderHasProducts = ref(false)
watch(tenderId, async (id) => {
  tenderHasProducts.value = false
  if (!id) return
  try {
    const t = await useApi<any>(`/tenders/${id}`)
    tenderHasProducts.value = (t.positions || []).some((pos: any) =>
      (pos.products || []).some((p: any) => p.is_selected || p.id === pos.selected_product_id)
    )
  } catch { /* не критично — влияет только на видимость кнопки */ }
}, { immediate: true })

const visibleQuickActions = computed(() =>
  TENDER_QUICK_ACTIONS.filter(a => a.kind !== 'check-products' || tenderHasProducts.value)
)

// «Ассистент думает» — показываем сразу по клику на кнопку действия, ещё до
// того как реальный запрос к /api/chat уйдёт (пока подгружаются данные
// тендера для контекста), и до конца стриминга ответа.
const isThinking = computed(() => quickActionLoading.value !== null || chat.isStreaming.value)

// Игра «Охотник на контракты» в правом сайдбаре (ChatSnakeGame.vue): появляется, когда ассистент начал отвечать
// в развёрнутом режиме, и остаётся (на паузе) после ответа, пока её не скроют крестиком.
// v-show, а не v-if: скрытие между ответами не сбрасывает начатую партию.
const snakeMounted = ref(false)
const snakeVisible = ref(false)
watch(isThinking, (thinking) => {
  if (thinking && chat.isExpanded.value) {
    snakeMounted.value = true
    snakeVisible.value = true
  }
})
// Карточка прогресса (вместо приветствия)
const progressView = computed(() => {
  const status = chat.progress.value.status
  if (status === 'running') return { label: 'Выполняется…', icon: 'lucide:loader-2', iconClass: 'text-primary-500', spin: true }
  if (status === 'question') return { label: 'Ждёт вашего ответа', icon: 'lucide:message-circle-question', iconClass: 'text-amber-500', spin: false }
  if (status === 'done') return { label: 'Завершено', icon: 'lucide:circle-check', iconClass: 'text-emerald-500', spin: false }
  if (status === 'error') return { label: 'Ошибка', icon: 'lucide:circle-alert', iconClass: 'text-error', spin: false }
  return { label: 'Готов к работе', icon: 'lucide:bot', iconClass: 'text-primary-500', spin: false }
})
const progressPercent = computed(() => {
  const { done, total } = chat.progress.value
  if (!total) return null
  return Math.min(100, Math.round(((done ?? 0) / total) * 100))
})

// Кнопка в шапке развёрнутого чата — открыть/скрыть игру вручную, не дожидаясь ответа
function toggleSnake() {
  snakeMounted.value = true
  snakeVisible.value = !snakeVisible.value
  if (snakeVisible.value) chat.closeHistory()
}

function summarizePositions(tender: any, forCheck: boolean): string {
  const positions = tender.positions || []
  const lines = positions.map((pos: any) => {
    const chars = (pos.characteristics || [])
      .map((c: any) => `${c.name}: ${c.value ?? c.required_value ?? ''}${c.unit ? ' ' + c.unit : ''}`.trim())
      .filter((s: string) => !s.endsWith(':'))
      .join('; ')
    const requirementPart = chars ? ` [требования: ${chars}]` : ''
    if (!forCheck) return `- ${pos.name}${requirementPart}`

    const selected = (pos.products || []).filter((p: any) => p.is_selected || p.id === pos.selected_product_id)
    const selectedPart = selected.length
      ? selected
          .map((p: any) => {
            const pChars = (p.characteristics || []).map((c: any) => `${c.name}: ${c.value}`).join('; ')
            return `выбран товар «${p.name}»${pChars ? ' (' + pChars + ')' : ''}`
          })
          .join('; ')
      : 'товар ещё не выбран'
    return `- ${pos.name}${requirementPart} — ${selectedPart}`
  })
  return lines.join('\n')
}

function buildTenderQuickActionText(tender: any, kind: TenderQuickActionKind): string {
  const title = tender.subject || tender.title || tender.name || `тендер ${tender.id}`
  const docsList = (tender.documents || []).map((d: any) => `- ${d.name}`).join('\n')
  const positionsSummary = summarizePositions(tender, kind === 'check-products' || kind === 'all')

  if (kind === 'all') {
    return [
      `Комплексно проанализируй тендер «${title}»: изучи документацию и помоги с товарами по позициям.`,
      docsList ? `Файлы документации:\n${docsList}` : 'Файлы документации в системе не загружены.',
      positionsSummary ? `Известные позиции, требования и уже выбранные товары:\n${positionsSummary}` : '',
      'Сначала кратко перечисли ключевые требования, сроки и особые условия по документации. Затем по каждой позиции: если товар ещё не выбран — предложи подходящие модели; если уже выбран — проверь его на соответствие требованиям и укажи, если есть несоответствия.',
    ].filter(Boolean).join('\n\n')
  }
  if (kind === 'docs') {
    return [
      `Изучи документацию по тендеру «${title}».`,
      docsList ? `Файлы документации:\n${docsList}` : 'Файлы документации в системе не загружены.',
      positionsSummary ? `Известные позиции и требования:\n${positionsSummary}` : '',
      'Кратко перечисли ключевые требования, сроки и особые условия, на что обратить внимание при подготовке заявки.',
    ].filter(Boolean).join('\n\n')
  }
  if (kind === 'find-products') {
    return [
      `Помоги подобрать товары для тендера «${title}» по следующим позициям и требованиям:`,
      positionsSummary || 'Характеристики позиций не указаны — ориентируйся на их названия.',
      'Предложи, какие товары или модели подойдут под каждую позицию, и на что обратить внимание при выборе.',
    ].join('\n\n')
  }
  return [
    `Проверь товары, выбранные для тендера «${title}», на соответствие требованиям:`,
    positionsSummary || 'Нет данных о требованиях или выбранных товарах по позициям.',
    'Укажи, какие позиции не соответствуют требованиям или вызывают сомнения, и почему.',
  ].join('\n\n')
}

async function runTenderAction(kind: TenderQuickActionKind) {
  if (!tenderId.value || quickActionLoading.value || chat.isStreaming.value) return
  quickActionLoading.value = kind
  try {
    await chat.open()
    await nextTick()
    const tender = await useApi<any>(`/tenders/${tenderId.value}`)
    const action = TENDER_QUICK_ACTIONS.find((a) => a.kind === kind)!
    const apiText = buildTenderQuickActionText(tender, kind)
    await chat.sendQuickAction(action.label, apiText)
  } catch (err) {
    console.error('[ChatFab] Tender quick action failed:', err)
  } finally {
    quickActionLoading.value = null
  }
}
</script>

<template>
  <div
    v-if="user"
    class="fixed right-4 z-50 h-12 w-12 transition-[bottom] duration-200 sm:right-6 lg:!bottom-6"
    :style="rootStyle"
  >
    <!-- Панель чата -->
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 translate-y-3 scale-95"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-2 scale-95"
    >
      <div v-if="chat.isOpen.value">
        <!-- Свёрнутая плашка (только маленький режим) — по умолчанию только иконка;
             при наведении выезжают название "Tezer" и крестик закрытия. Тап без сдвига
             указателя (см. startDrag/onMinimizedPointerDown) разворачивает обратно.
             Использует ту же позицию/перетаскивание, что и полная панель (общий
             panelStyle), поэтому остаётся там же, где её свернули. -->
        <div
          v-if="chat.isMinimized.value && !chat.isExpanded.value"
          ref="panelEl"
          class="group fixed z-50 flex cursor-pointer select-none items-center rounded-full bg-primary-500 p-2.5 shadow-[0_10px_24px_-6px_rgba(41,107,224,0.6)] sm:absolute sm:inset-auto sm:bottom-0 sm:right-0 sm:cursor-move"
          :style="panelStyle"
          @pointerdown="onMinimizedPointerDown"
        >
          <div class="relative shrink-0">
            <div class="flex h-[38px] w-[38px] flex-none items-center justify-center">
              <IconsChatAi class="h-[20px] w-[20px] text-white" />
            </div>
            <span
              v-if="chat.unread.value > 0"
              class="absolute -right-1 -top-1 flex h-[16px] min-w-[16px] items-center justify-center rounded-full border-2 border-white px-0.5 text-[9px] font-bold text-white"
              style="background:#c9040f"
            >{{ chat.unread.value > 9 ? '9+' : chat.unread.value }}</span>
          </div>
          <div class="flex max-w-0 items-center gap-1 overflow-hidden opacity-0 transition-[max-width,opacity,margin-left] duration-150 group-hover:ml-2.5 group-hover:max-w-[170px] group-hover:opacity-100">
            <span class="whitespace-nowrap text-compact font-semibold text-white">Tezer</span>
            <UButton
              icon="lucide:maximize-2"
              color="neutral"
              variant="ghost"
              size="xs"
              class="ml-1 shrink-0 text-white/70 hover:bg-white/15 hover:text-white"
              aria-label="Развернуть чат"
              title="Развернуть чат"
              @click="chat.restore()"
            />
            <UButton
              icon="lucide:x"
              color="neutral"
              variant="ghost"
              size="xs"
              class="shrink-0 text-white/70 hover:bg-white/15 hover:text-white"
              aria-label="Закрыть чат"
              title="Закрыть чат"
              @click="chat.close()"
            />
          </div>
        </div>

        <!-- Полная панель -->
        <div
          v-else
          ref="panelEl"
          :class="[
            'fixed inset-0 z-50 flex h-[100dvh] w-screen flex-col overflow-hidden bg-white dark:bg-gray-900',
            chat.isExpanded.value
              ? 'shadow-sm md:fixed md:inset-y-0 md:right-0 md:left-auto md:h-dvh md:w-[var(--chat-width)] md:max-w-[92vw] md:rounded-none md:border-l md:border-default md:dark:border-gray-800'
              : 'shadow-2xl sm:absolute sm:inset-auto sm:bottom-0 sm:right-0 sm:h-[min(560px,calc(100vh-96px))] sm:w-[min(380px,calc(100vw-24px))] sm:rounded-2xl sm:border sm:border-default sm:dark:border-gray-800'
          ]"
          :style="panelStyle"
        >
          <!-- Ручка изменения ширины (только в полноразмерном режиме, десктоп) -->
          <div
            v-if="chat.isExpanded.value"
            class="absolute inset-y-0 left-0 z-10 hidden w-1.5 -translate-x-1/2 cursor-col-resize md:block"
            :class="chat.isResizing.value ? 'bg-primary-400/60' : 'hover:bg-primary-400/40'"
            @pointerdown="onResizeStart"
          />

          <!-- Шапка. В маленьком режиме — ещё и "ручка" перетаскивания (курсор move,
               pointerdown запускает onDragStart; клики по кнопкам внутри игнорируются
               обработчиком). -->
          <div
            :class="[
              'relative flex flex-none items-center justify-between gap-3 border-b border-default bg-white dark:border-gray-800 dark:bg-gray-900',
              chat.isExpanded.value ? 'h-16 px-4' : 'p-4 sm:cursor-move'
            ]"
            @pointerdown="onDragStart"
          >
            <div class="flex items-center gap-2.5">
              <div>
                <div class="text-lg font-bold leading-tight text-primary-500 dark:text-primary-400">
                  Tezer
                </div>
                <div v-if="chat.isStreaming.value" class="mt-0.5 text-micro leading-tight text-gray-500 dark:text-gray-400">
                  Печатает…
                </div>
              </div>
            </div>
            <div class="flex items-center gap-1">
              <UButton
                v-if="chat.isExpanded.value"
                icon="lucide:gamepad-2"
                color="neutral"
                :variant="snakeVisible ? 'soft' : 'ghost'"
                size="sm"
                :aria-label="snakeVisible ? 'Скрыть игру' : 'Открыть игру'"
                :aria-pressed="snakeVisible"
                :title="snakeVisible ? 'Скрыть игру' : 'Открыть игру'"
                @click="toggleSnake"
              />
              <UButton
                v-if="!chat.isExpanded.value"
                icon="lucide:minus"
                color="neutral"
                variant="ghost"
                size="sm"
                aria-label="Свернуть в плашку"
                title="Свернуть в плашку"
                @click="chat.minimize()"
              />
              <UButton
                :icon="chat.showHistory.value ? 'lucide:message-square' : 'lucide:history'"
                color="neutral"
                variant="ghost"
                size="sm"
                :aria-label="chat.showHistory.value ? 'К чату' : 'История чатов'"
                :title="chat.showHistory.value ? 'К чату' : 'История чатов'"
                @click="chat.showHistory.value ? chat.closeHistory() : chat.openHistory()"
              />
              <UButton
                :icon="chat.isExpanded.value ? 'lucide:minimize-2' : 'lucide:maximize-2'"
                color="neutral"
                variant="ghost"
                size="sm"
                class="hidden md:inline-flex"
                :aria-label="chat.isExpanded.value ? 'Свернуть чат' : 'Открыть чат на весь экран'"
                :title="chat.isExpanded.value ? 'Свернуть чат' : 'Открыть чат на весь экран'"
                @click="chat.toggleExpand()"
              />
              <UButton
                icon="lucide:x"
                color="neutral"
                variant="ghost"
                size="sm"
                aria-label="Закрыть чат"
                title="Закрыть чат"
                @click="chat.close()"
              />
            </div>
          </div>

        <!-- Тело чата: история или лента сообщений; игра — слоем поверх всего тела
             (от шапки сайдбара до поля ввода, на всю ширину), не сдвигая переписку. -->
        <div class="relative flex min-h-0 flex-1 flex-col">
        <!-- История чатов — список прошлых бесед прямо в теле панели, вместо ленты
             сообщений (переключается кнопкой "История"/"К чату" в шапке). -->
        <div
          v-if="chat.showHistory.value"
          class="flex min-h-0 flex-1 flex-col overflow-y-auto bg-gray-50 dark:bg-gray-950"
        >
          <div class="flex flex-none items-center justify-between border-b border-default bg-white px-4 py-2.5 dark:border-gray-800 dark:bg-gray-900">
            <span class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">История чатов</span>
            <button
              type="button"
              class="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
              @click="chat.startNewConversation()"
            >
              <Icon name="lucide:plus" class="h-3.5 w-3.5" />
              Новый чат
            </button>
          </div>

          <div v-if="chat.loadingConversations.value" class="flex flex-1 items-center justify-center">
            <Icon name="lucide:loader-2" class="h-5 w-5 animate-spin text-gray-300 dark:text-gray-600" />
          </div>
          <div v-else-if="chat.conversations.value.length === 0" class="flex flex-1 items-center justify-center px-6 text-center text-compact text-gray-500 dark:text-gray-400">
            Пока нет сохранённых чатов
          </div>
          <div v-else class="flex flex-1 flex-col divide-y divide-gray-100 dark:divide-gray-800">
            <div
              v-for="conv in chat.conversations.value"
              :key="conv.id"
              class="group flex items-center gap-1 px-4 py-3 hover:bg-white dark:hover:bg-gray-900"
              :class="conv.id === chat.chatId.value ? 'bg-white dark:bg-gray-900' : ''"
            >
              <button
                type="button"
                class="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left"
                @click="chat.openConversation(conv.id)"
              >
                <span class="w-full truncate text-compact font-medium text-gray-900 dark:text-white">{{ conv.title || 'Без названия' }}</span>
                <span class="text-micro text-gray-500 dark:text-gray-400">{{ formatDateOrTime(conv.updated_at) }}</span>
              </button>
              <UButton
                icon="lucide:trash-2"
                color="neutral"
                variant="ghost"
                size="xs"
                class="flex-none opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 hover:text-error"
                title="Удалить чат"
                aria-label="Удалить чат"
                @click.stop="confirmDeleteConversation(conv.id)"
              />
            </div>
          </div>
        </div>

        <!-- Лента сообщений -->
        <div
          v-else
          :ref="(el) => (chat.scrollEl.value = el as HTMLElement | null)"
          class="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto bg-gray-50 p-4 dark:bg-gray-950"
        >
          <template v-if="chat.assistantsLoaded.value && !chat.hasUsableAssistant.value && chat.messages.value.length === 0">
            <div class="flex flex-1 items-center justify-center">
              <div class="max-w-[240px] text-center text-compact leading-relaxed text-gray-500 dark:text-gray-400">
                Ассистент ещё не настроен. Обратитесь к администратору, чтобы подключить AI-ассистента.
              </div>
            </div>
          </template>
          <template v-else-if="!chat.assistantsLoaded.value && chat.messages.value.length === 0">
            <div class="flex flex-1 items-center justify-center">
              <Icon name="lucide:loader-2" class="h-5 w-5 animate-spin text-gray-300 dark:text-gray-600" />
            </div>
          </template>
          <template v-else>
            <!-- Прогресс работы ассистента (вместо приветствия): статус, позиции, подобранные товары.
                 Данные — из служебной разметки ответа (utils/assistantMarkup.ts). -->
            <div
              class="sticky top-0 z-10 -mx-1 rounded-xl border border-default bg-white/95 px-3 py-2.5 shadow-sm backdrop-blur dark:border-gray-800 dark:bg-gray-900/95"
              role="status"
              aria-live="polite"
            >
              <div class="flex items-center justify-between gap-2">
                <div class="flex min-w-0 items-center gap-2">
                  <Icon
                    :name="progressView.icon"
                    :class="['h-4 w-4 flex-none', progressView.iconClass, progressView.spin && 'animate-spin']"
                  />
                  <span class="truncate text-compact font-semibold text-gray-900 dark:text-white">{{ progressView.label }}</span>
                </div>
                <span v-if="chat.progress.value.products !== null" class="flex-none text-micro text-gray-500 dark:text-gray-400">
                  Подобрано товаров: <b class="text-gray-900 dark:text-white">{{ chat.progress.value.products }}</b>
                </span>
              </div>
              <template v-if="progressPercent !== null">
                <div class="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                  <div
                    class="h-full rounded-full transition-[width] duration-500"
                    :class="chat.progress.value.status === 'done' ? 'bg-emerald-500' : 'bg-primary-500'"
                    :style="{ width: `${progressPercent}%` }"
                  />
                </div>
                <div class="mt-1 text-micro text-gray-500 dark:text-gray-400">
                  Позиции: {{ chat.progress.value.done ?? 0 }} из {{ chat.progress.value.total }}
                </div>
              </template>
              <p v-else-if="chat.progress.value.status === 'idle'" class="mt-1 text-micro leading-relaxed text-gray-500 dark:text-gray-400">
                Найдите тендер, проверьте товары по ТЗ или разберитесь с документацией — здесь будет виден ход работы.
              </p>
            </div>

            <div
              v-for="message in chat.messages.value"
              :key="message.id"
              :class="message.role === 'user' ? 'flex justify-end' : 'flex items-end gap-1.5'"
            >
              <span
                v-if="message.role === 'assistant'"
                class="flex h-[26px] w-[26px] flex-none items-center justify-center rounded-[7px] bg-primary-50 dark:bg-primary-900/40"
              >
                <Icon name="lucide:bot" class="h-3 w-3 text-primary-600 dark:text-primary-400" />
              </span>
              <div
                class="max-w-[82%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-compact leading-relaxed shadow-sm"
                :class="message.role === 'user'
                  ? 'rounded-br-md bg-primary-500 text-white'
                  : 'rounded-bl-md border border-default bg-white text-gray-900 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-100'"
              >
                <div v-if="message.attachments?.length" class="mb-1.5 flex flex-col gap-1.5">
                  <template v-for="(attachment, i) in message.attachments" :key="i">
                    <img
                      v-if="attachment.isImage && attachment.previewUrl"
                      :src="attachment.previewUrl"
                      :alt="attachment.name"
                      class="block max-h-[180px] max-w-full rounded-lg object-cover"
                    >
                    <div
                      v-else
                      class="flex items-center gap-2 rounded-lg px-2.5 py-2 text-xs"
                      :class="message.role === 'user' ? 'bg-white/15' : 'bg-gray-100 dark:bg-gray-800'"
                    >
                      <Icon name="lucide:file" class="h-3.5 w-3.5 flex-none opacity-70" />
                      <span class="min-w-0 flex-1 truncate">{{ attachment.name }}</span>
                    </div>
                  </template>
                </div>
                <template v-if="message.content">{{ message.content }}</template>
              </div>
            </div>

            <div
              v-if="isThinking"
              class="flex items-end gap-1.5"
            >
              <span class="flex h-[26px] w-[26px] flex-none items-center justify-center rounded-[7px] bg-primary-50 dark:bg-primary-900/40">
                <Icon name="lucide:bot" class="h-3 w-3 text-primary-600 dark:text-primary-400" />
              </span>
              <div class="max-w-[82%] rounded-2xl rounded-bl-md border border-default bg-white px-3.5 py-2.5 text-compact leading-relaxed text-gray-900 shadow-sm dark:border-gray-800 dark:bg-gray-900 dark:text-gray-100">
                <div v-if="chat.streamingText.value" class="whitespace-pre-wrap">{{ chat.streamingText.value }}</div>
                <div v-else class="flex items-center gap-1">
                  <span class="h-1.5 w-1.5 animate-pulse rounded-full bg-gray-400" style="animation-delay:0ms" />
                  <span class="h-1.5 w-1.5 animate-pulse rounded-full bg-gray-400" style="animation-delay:300ms" />
                  <span class="h-1.5 w-1.5 animate-pulse rounded-full bg-gray-400" style="animation-delay:600ms" />
                </div>
              </div>
            </div>

            <!-- Варианты ответа на вопрос ассистента -->
            <div
              v-if="chat.pendingQuestion.value?.options.length && !isThinking"
              class="flex flex-wrap gap-1.5 pl-8"
            >
              <button
                v-for="option in chat.pendingQuestion.value.options"
                :key="option"
                type="button"
                class="rounded-full border border-primary-200 bg-white px-3 py-1.5 text-compact font-medium text-primary-700 hover:bg-primary-50 dark:border-primary-800 dark:bg-gray-900 dark:text-primary-300 dark:hover:bg-primary-900/30"
                @click="chat.answerQuestion(option)"
              >
                {{ option }}
              </button>
            </div>
          </template>
        </div>

        <ChatSnakeGame
          v-if="snakeMounted && chat.isExpanded.value"
          v-show="snakeVisible && !chat.showHistory.value"
          class="absolute inset-0 z-20"
          :active="isThinking"
          :visible="snakeVisible && !chat.showHistory.value"
          :question="isThinking ? null : chat.pendingQuestion.value"
          @answer="chat.answerQuestion"
          @close="snakeVisible = false"
        />
        </div>

        <!-- Действия для тендера -->
        <div
          v-if="isTenderDetailPage && !chat.showHistory.value"
          class="flex flex-none flex-col gap-1.5 border-t border-default bg-white px-3 pb-2.5 pt-2 dark:border-gray-800 dark:bg-gray-900"
        >
          <div class="flex items-center justify-between">
            <span class="text-micro font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Действия</span>
            <UButton
              :icon="quickActionsCollapsed ? 'lucide:chevron-down' : 'lucide:chevron-up'"
              color="neutral"
              variant="ghost"
              size="xs"
              :aria-label="quickActionsCollapsed ? 'Развернуть действия' : 'Свернуть действия'"
              :aria-expanded="!quickActionsCollapsed"
              :title="quickActionsCollapsed ? 'Развернуть действия' : 'Свернуть действия'"
              @click="quickActionsCollapsed = !quickActionsCollapsed"
            />
          </div>
          <div v-if="!quickActionsCollapsed" class="flex flex-wrap gap-1.5">
            <button
              v-for="action in visibleQuickActions"
              :key="action.kind"
              type="button"
              class="flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1.5 text-compact font-medium text-primary-700 hover:bg-primary-100 disabled:opacity-50 dark:bg-primary-900/30 dark:text-primary-300 dark:hover:bg-primary-900/50"
              :disabled="!chat.hasUsableAssistant.value || chat.isStreaming.value || quickActionLoading !== null"
              @click="runTenderAction(action.kind)"
            >
              <Icon
                :name="quickActionLoading === action.kind ? 'lucide:loader-2' : action.icon"
                :class="['h-3.5 w-3.5', quickActionLoading === action.kind && 'animate-spin']"
              />
              {{ action.label }}
            </button>
          </div>
        </div>

        <!-- Прикреплённые файлы (ещё не отправлены) -->
        <div
          v-if="!chat.showHistory.value && (chat.pendingAttachments.value.length || chat.attachmentError.value)"
          class="flex flex-none flex-col gap-1.5 border-t border-default bg-white px-3 pt-2.5 dark:border-gray-800 dark:bg-gray-900"
        >
          <div
            v-for="(attachment, index) in chat.pendingAttachments.value"
            :key="index"
            class="flex items-center gap-2 rounded-lg bg-gray-100 px-2.5 py-1.5 dark:bg-gray-800"
          >
            <img
              v-if="attachment.isImage && attachment.previewUrl"
              :src="attachment.previewUrl"
              alt=""
              class="h-8 w-8 flex-none rounded-md object-cover"
            >
            <Icon v-else name="lucide:file" class="h-4 w-4 flex-none text-gray-400" />
            <div class="min-w-0 flex-1">
              <div class="truncate text-compact font-medium text-gray-700 dark:text-gray-200">{{ attachment.name }}</div>
              <div class="text-micro text-gray-500">{{ formatFileSize(attachment.size) }}</div>
            </div>
            <UButton
              icon="lucide:x"
              color="neutral"
              variant="ghost"
              size="xs"
              class="flex-none"
              aria-label="Убрать файл"
              title="Убрать файл"
              @click="chat.removeAttachment(index)"
            />
          </div>
          <p v-if="chat.attachmentError.value" class="pb-1 text-xs text-red-500">{{ chat.attachmentError.value }}</p>
        </div>

        <!-- Поле ввода -->
        <div v-if="!chat.showHistory.value" class="flex flex-none items-end gap-1.5 border-t border-default bg-white p-3 dark:border-gray-800 dark:bg-gray-900">
          <input
            :ref="(el) => (chat.fileInputEl.value = el as HTMLInputElement | null)"
            type="file"
            multiple
            class="hidden"
            accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,image/jpeg,image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            @change="chat.onFileChange"
          >
          <UButton
            icon="lucide:paperclip"
            color="neutral"
            variant="ghost"
            class="flex-none"
            :disabled="!chat.hasUsableAssistant.value || chat.isStreaming.value"
            aria-label="Прикрепить файл"
            title="Прикрепить файл"
            @click="chat.pickFile()"
          />
          <textarea aria-label="Сообщение"
            :ref="(el) => (chat.draftEl.value = el as HTMLTextAreaElement | null)"
            v-model="chat.draft.value"
            rows="1"
            :disabled="!chat.hasUsableAssistant.value || chat.isStreaming.value"
            placeholder="Напишите сообщение…"
            class="max-h-[136px] min-w-0 flex-1 resize-none rounded-lg border border-default bg-gray-50 px-3 py-2.5 text-compact leading-snug text-gray-900 outline-none placeholder:text-gray-400 focus:border-primary-400 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
            @keydown="chat.onDraftKeydown"
          />
          <UButton
            icon="lucide:send"
            square
            class="flex-none"
            :disabled="(!chat.draft.value.trim() && !chat.pendingAttachments.value.length) || !chat.hasUsableAssistant.value || chat.isStreaming.value"
            aria-label="Отправить"
            title="Отправить"
            @click="chat.send()"
          />
        </div>
        </div>
      </div>
    </Transition>

    <!-- Больше нет своей плавающей кнопки/подсказки — открывается иконкой sparkles в
         шапке (см. LayoutHeaderActions.vue). Этот div остаётся как якорь позиционирования
         для самой панели (fixed/absolute-координаты выше завязаны на него). -->
  </div>
</template>
